import { useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/button/Button";
import type {
  CalibrationResolutionOutcome,
  CheckAnswer,
  CheckDisagreementDetail,
} from "@/services/api/agentReview";
import { answerLabel } from "./calibrationAnalysisHelpers";

function AnswerBadge({ answer }: { answer: CheckAnswer }) {
  return (
    <span
      className={clsx(
        "inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ring-1",
        answer === "pass" && "bg-emerald-500/10 text-emerald-200 ring-emerald-500/25",
        answer === "fail" && "bg-rose-500/10 text-rose-200 ring-rose-500/25",
        answer === "na" && "bg-slate-500/10 text-slate-300 ring-slate-500/25",
      )}
    >
      {answerLabel(answer)}
    </span>
  );
}

function DisputeAnswers({ item }: { item: CheckDisagreementDetail }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
      <span className="flex items-center gap-1.5 text-slate-400">
        Human
        <AnswerBadge answer={item.human_answer} />
      </span>
      <span className="flex items-center gap-1.5 text-slate-500">
        Ace
        <AnswerBadge answer={item.ace_answer} />
      </span>
    </div>
  );
}

const OUTCOME_OPTIONS: {
  outcome: CalibrationResolutionOutcome;
  label: string;
  description: string;
}[] = [
  {
    outcome: "human_correct",
    label: "Human correct",
    description: "Ace should adopt the human standard",
  },
  {
    outcome: "ace_correct",
    label: "Ace correct",
    description: "Ace applied the right bar",
  },
  {
    outcome: "resolved",
    label: "Resolved",
    description: "Closed after re-review or criteria update",
  },
];

function outcomeLabel(outcome: CalibrationResolutionOutcome): string {
  return OUTCOME_OPTIONS.find((option) => option.outcome === outcome)?.label ?? outcome;
}

const ALL_OUTCOMES: CalibrationResolutionOutcome[] = ["human_correct", "ace_correct", "resolved"];

export function CalibrationResolutionControls({
  item,
  saving,
  onResolve,
  onClear,
  caseCount = 1,
  allowedOutcomes = ALL_OUTCOMES,
  bulkResolvedSummary,
}: {
  item: CheckDisagreementDetail;
  saving: boolean;
  onResolve: (outcome: CalibrationResolutionOutcome, note: string) => void;
  onClear: () => void;
  caseCount?: number;
  allowedOutcomes?: CalibrationResolutionOutcome[];
  bulkResolvedSummary?: "open" | "all_closed" | "partial_closed";
}) {
  const [note, setNote] = useState(item.resolution_note ?? "");
  const bulk = caseCount > 1;
  const bulkResolvedOnly =
    allowedOutcomes.length === 1 && allowedOutcomes[0] === "resolved";
  const options = OUTCOME_OPTIONS.filter((option) => allowedOutcomes.includes(option.outcome));

  const showResolvedStatus = bulkResolvedOnly
    ? bulkResolvedSummary === "all_closed" && item.resolution_outcome === "resolved"
    : Boolean(
        item.resolution_outcome &&
          allowedOutcomes.includes(item.resolution_outcome),
      );

  if (showResolvedStatus && item.resolution_outcome) {
    return (
      <div
        className="rounded-lg bg-emerald-500/[0.06] px-3 py-2.5 ring-1 ring-emerald-500/20 space-y-2"
        data-testid="calibration-resolution-status"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-emerald-200">
            {bulkResolvedOnly && bulk
              ? `All ${caseCount} cases resolved: `
              : "Resolved: "}
            <span className="font-medium">{outcomeLabel(item.resolution_outcome)}</span>
          </p>
          <Button
            color="slate"
            disabled={saving}
            onClick={onClear}
            data-testid="calibration-resolution-reopen"
          >
            {bulkResolvedOnly && bulk ? "Re-open all" : "Re-open"}
          </Button>
        </div>
        {!bulkResolvedOnly ? <DisputeAnswers item={item} /> : null}
        {item.resolution_note?.trim() ? (
          <p className="text-xs text-slate-400 leading-relaxed">{item.resolution_note}</p>
        ) : null}
      </div>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2" data-testid="calibration-resolution-controls">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {bulkResolvedOnly
          ? bulkResolvedSummary === "partial_closed"
            ? `Close remaining cases (${caseCount} total)`
            : `Close all ${caseCount} cases`
          : "Resolve dispute"}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.outcome}
            type="button"
            disabled={saving}
            onClick={() => onResolve(option.outcome, note)}
            className={clsx(
              "rounded-lg px-3 py-2 text-left ring-1 transition-colors disabled:opacity-50",
              option.outcome === "human_correct" &&
                "bg-slate-500/10 text-slate-200 ring-slate-500/25 hover:bg-slate-500/15",
              option.outcome === "ace_correct" &&
                "bg-indigo-500/10 text-indigo-100 ring-indigo-500/25 hover:bg-indigo-500/15",
              option.outcome === "resolved" &&
                "bg-emerald-500/10 text-emerald-100 ring-emerald-500/25 hover:bg-emerald-500/15",
            )}
            data-testid={`calibration-resolution-${option.outcome}`}
          >
            <span className="block text-xs font-medium">{option.label}</span>
            <span className="block text-[11px] opacity-80 mt-1">{option.description}</span>
            {!bulkResolvedOnly ? (
              <span className="block mt-2 pt-2 border-t border-white/10">
                <DisputeAnswers item={item} />
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <label className="block">
        <span className="sr-only">Resolution note</span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          placeholder="Optional note — what should Ace or reviewers do differently?"
          className="w-full rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-slate-200 ring-1 ring-white/10 placeholder:text-slate-600 focus:outline-none focus:ring-indigo-500/40"
        />
      </label>
    </div>
  );
}

export { outcomeLabel };
